import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AvailabilitySlot,
  BusinessCalendarDay,
  DashboardResponse,
  GuestHistoryView,
  ReservationDetailView,
  ReservationInquiry,
  ReservationListItem,
  ReservationMenu,
  ReservationSeatType,
  TimelineView,
} from '../../types/reservation';
import {
  fetchGuestHistory,
  fetchInquiriesForStaff,
  fetchReservationDashboard,
  fetchReservationDetail,
  fetchReservationMenusForStaff,
  fetchReservations,
  fetchSeatTypesForAssignment,
  fetchStaffAvailability,
  fetchStaffBusinessCalendar,
  fetchTimeline,
  updateStaffNote,
} from './reservationApi';

interface ReservationState {
  list: ReservationListItem[];
  listLoading: boolean;
  listError: string | null;
  detail: ReservationDetailView | null;
  detailLoading: boolean;
  detailError: string | null;
  timeline: TimelineView | null;
  timelineLoading: boolean;
  timelineError: string | null;
  guestHistory: GuestHistoryView | null;
  guestHistoryLoading: boolean;
  seatTypes: ReservationSeatType[];
  menus: ReservationMenu[];
  inquiries: ReservationInquiry[];
  availability: AvailabilitySlot[];
  availabilityLoading: boolean;
  businessCalendar: BusinessCalendarDay[];
  // ダッシュボード集計（統合契約 B）
  dashboard: DashboardResponse | null;
  dashboardLoading: boolean;
  dashboardError: string | null;
}

const initialState: ReservationState = {
  list: [],
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  detailError: null,
  timeline: null,
  timelineLoading: false,
  timelineError: null,
  guestHistory: null,
  guestHistoryLoading: false,
  seatTypes: [],
  menus: [],
  inquiries: [],
  availability: [],
  availabilityLoading: false,
  businessCalendar: [],
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,
};

const reservationSlice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    /** 詳細画面を離れるときに前回の詳細・顧客履歴をクリアする */
    clearReservationDetail: (state) => {
      state.detail = null;
      state.detailError = null;
      state.guestHistory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReservations.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchReservations.fulfilled, (state, action) => {
        state.list = action.payload;
        state.listLoading = false;
      })
      .addCase(fetchReservations.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload as string;
      });

    builder
      .addCase(fetchReservationDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchReservationDetail.fulfilled, (state, action) => {
        state.detail = action.payload;
        state.detailLoading = false;
      })
      .addCase(fetchReservationDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload as string;
      });

    builder
      .addCase(fetchTimeline.pending, (state) => {
        state.timelineLoading = true;
        state.timelineError = null;
      })
      .addCase(fetchTimeline.fulfilled, (state, action: PayloadAction<TimelineView>) => {
        state.timeline = action.payload;
        state.timelineLoading = false;
      })
      .addCase(fetchTimeline.rejected, (state, action) => {
        state.timelineLoading = false;
        state.timelineError = action.payload as string;
      });

    builder
      .addCase(fetchGuestHistory.pending, (state) => {
        state.guestHistoryLoading = true;
      })
      .addCase(fetchGuestHistory.fulfilled, (state, action) => {
        state.guestHistory = action.payload;
        state.guestHistoryLoading = false;
      })
      .addCase(fetchGuestHistory.rejected, (state) => {
        state.guestHistoryLoading = false;
        state.guestHistory = null;
      });

    // メモ更新は成功時に詳細へ即時反映（再取得不要）
    builder.addCase(updateStaffNote.fulfilled, (state, action) => {
      if (state.detail) {
        state.detail.staffNote = action.payload.staffNote;
      }
    });

    builder.addCase(fetchSeatTypesForAssignment.fulfilled, (state, action) => {
      state.seatTypes = action.payload;
    });
    builder.addCase(fetchReservationMenusForStaff.fulfilled, (state, action) => {
      state.menus = action.payload;
    });
    builder.addCase(fetchInquiriesForStaff.fulfilled, (state, action) => {
      state.inquiries = action.payload;
    });

    builder
      .addCase(fetchStaffAvailability.pending, (state) => {
        state.availabilityLoading = true;
      })
      .addCase(fetchStaffAvailability.fulfilled, (state, action) => {
        state.availability = action.payload;
        state.availabilityLoading = false;
      })
      .addCase(fetchStaffAvailability.rejected, (state) => {
        state.availabilityLoading = false;
      });

    builder.addCase(fetchStaffBusinessCalendar.fulfilled, (state, action) => {
      state.businessCalendar = action.payload;
    });

    builder
      .addCase(fetchReservationDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchReservationDashboard.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.dashboardLoading = false;
      })
      .addCase(fetchReservationDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload as string;
      });
  },
});

export const { clearReservationDetail } = reservationSlice.actions;

export default reservationSlice.reducer;
